/**
 * Types and service wrappers for owner/sub-profile relationship data.
 */

import {
  getOwnerDependentPoliciesEndPoint,
  getOwnerLinkedPoliciesEndPoint,
  getOwnerProfileEndPoint,
} from '../api.endPoints';
import { ExistingPolicy, PastApplication } from '../dataTypes';
import { HttpService } from '../http.service';

export interface ProfileRelationshipRef {
  primaryProfileId?: number;
  subProfileId?: number;
  relationshipType: string;
  status: 'active' | 'transferred';
}

export interface ProfilePersonResponse {
  id: number;
  profileType: string;
  nameFirst: string;
  nameLast: string;
  nameMiddle?: string;
  birthDate: string;
  gender: string;
  emails?: { emailAddress: string; emailType: string }[];
  phones?: { phoneNumber: string; phoneType: string }[];
  addresses?: {
    street1: string;
    city: string;
    state: string;
    zip: string;
  }[];
  dependents: ProfileRelationshipRef[];
  owners: ProfileRelationshipRef[];
}

export interface OwnerLinkedPoliciesResponse {
  activatedPolicies: ExistingPolicy[];
  maestroPolicies: PastApplication[];
}

/**
 * Fetches the owner profile for a juvenile (sub-profile) application.
 *
 * Calls GET /applications/:id/ownerprofile on Nora backend.
 */
export async function getOwnerProfile(
  applicationId: string,
): Promise<ProfilePersonResponse | null> {
  try {
    const response = await HttpService.getInstance().get(
      getOwnerProfileEndPoint(applicationId),
    );
    if (response && !response.error && response.parsedBody?.id) {
      return response.parsedBody;
    }
  } catch {
    return null;
  }

  return null;
}

/**
 * Fetches policies linked to the owner identity for the Owner section on juvenile workbench applications.
 *
 * Calls GET /applications/:id/ownerlinkedpolicies on Nora backend.
 */
export async function getOwnerLinkedPolicies(
  applicationId: string,
): Promise<OwnerLinkedPoliciesResponse> {
  try {
    const response = await HttpService.getInstance().get(
      getOwnerLinkedPoliciesEndPoint(applicationId),
    );
    if (response && !response.error) {
      const data = response.parsedBody;
      const hasData =
        data?.activatedPolicies?.length > 0 ||
        data?.maestroPolicies?.length > 0;
      if (hasData) {
        return data;
      }
    }
  } catch {
    return { activatedPolicies: [], maestroPolicies: [] };
  }

  return { activatedPolicies: [], maestroPolicies: [] };
}

/**
 * Fetches policies where the owner is the owner but not the insured (the Dependents section).
 *
 * Calls GET /applications/:id/ownerdependentpolicies on Nora backend.
 */
export async function getOwnerDependentPolicies(
  applicationId: string,
): Promise<OwnerLinkedPoliciesResponse> {
  try {
    const response = await HttpService.getInstance().get(
      getOwnerDependentPoliciesEndPoint(applicationId),
    );
    if (response && !response.error) {
      const data = response.parsedBody;
      const hasData =
        data?.activatedPolicies?.length > 0 ||
        data?.maestroPolicies?.length > 0;
      if (hasData) {
        return data;
      }
    }
  } catch {
    return { activatedPolicies: [], maestroPolicies: [] };
  }

  return { activatedPolicies: [], maestroPolicies: [] };
}
